import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';

export const AuthContext = createContext();

const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes in ms
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
const API_BASE = process.env.REACT_APP_API_URL || 'https://literent-backend.onrender.com';
const LAST_ACTIVE_KEY = 'lastActive';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);

  const inactivityTimer = useRef(null);

  // ─── Core logout ──────────────────────────────────────────────
  const logout = useCallback((reason = '') => {
    if (reason) console.log(`[Auth] Logged out: ${reason}`);
    setUser(null);
    setIsLoggedIn(false);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem(LAST_ACTIVE_KEY);
    clearTimeout(inactivityTimer.current);
  }, []);

  // ─── Inactivity timer ─────────────────────────────────────────
  const resetInactivityTimer = useCallback(() => {
    localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString()); // persist across refreshes
    clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      logout('30 minutes of inactivity');
    }, INACTIVITY_LIMIT);
  }, [logout]);

  // ─── Attach/detach activity listeners ─────────────────────────
  const startActivityTracking = useCallback(() => {
    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, resetInactivityTimer, { passive: true })
    );
    resetInactivityTimer(); // start the first timer immediately
  }, [resetInactivityTimer]);

  const stopActivityTracking = useCallback(() => {
    ACTIVITY_EVENTS.forEach(event =>
      window.removeEventListener(event, resetInactivityTimer)
    );
    clearTimeout(inactivityTimer.current);
  }, [resetInactivityTimer]);

  // ─── Validate token with server on every app load ─────────────
  // This ensures deleted users, role-changed users, etc. are kicked out
  const validateTokenWithServer = useCallback(async (token) => {
    try {
      const res = await fetch(`${API_BASE}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401 || res.status === 403) {
        // Token invalid or user deleted — force logout
        logout('Token rejected by server');
        return false;
      }

      if (res.ok) {
        const data = await res.json();
        // Refresh stored user data with latest from server
        if (data?.profile) {
          const refreshedUser = {
            ...JSON.parse(localStorage.getItem('user') || '{}'),
            fullName: data.profile.full_name,
            email: data.profile.email,
            phone: data.profile.phone,
            role: data.profile.role,
            permissions: data.profile.permissions || [],
          };
          localStorage.setItem('user', JSON.stringify(refreshedUser));
          setUser(refreshedUser);
        }
        return true;
      }

      return false;
    } catch (err) {
      // Network error — don't log out, could be temporary
      console.warn('[Auth] Could not validate token (network error), keeping session.');
      return true;
    }
  }, [logout]);

  // ─── On mount: check localStorage + validate with server ──────
  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('user');

      if (!token || !savedUser) {
        setLoading(false);
        return;
      }

      // ── Check if user was inactive for 30+ mins before this page load ──
      const lastActive = localStorage.getItem(LAST_ACTIVE_KEY);
      if (lastActive && Date.now() - parseInt(lastActive, 10) > INACTIVITY_LIMIT) {
        logout('Session expired due to inactivity');
        setLoading(false);
        return;
      }

      // Check JWT expiry locally first (fast, no network needed)
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp && Date.now() / 1000 > payload.exp) {
          logout('JWT expired');
          setLoading(false);
          return;
        }
      } catch {
        logout('Malformed token');
        setLoading(false);
        return;
      }

      // Parse saved user optimistically so UI loads fast
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setIsLoggedIn(true);
      } catch {
        logout('Corrupt user data');
        setLoading(false);
        return;
      }

      // Then validate with server in background
      const valid = await validateTokenWithServer(token);
      if (valid) {
        setIsLoggedIn(true);
        startActivityTracking();
      }

      setLoading(false);
    };

    init();

    return () => stopActivityTracking();
  }, [logout, validateTokenWithServer, startActivityTracking, stopActivityTracking]);

  // ─── Login ────────────────────────────────────────────────────
  const login = useCallback((userData, token) => {
    setUser(userData);
    setIsLoggedIn(true);
    localStorage.setItem('authToken', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
    startActivityTracking();
  }, [startActivityTracking]);

  // ─── Stop tracking when logged out ────────────────────────────
  useEffect(() => {
    if (!isLoggedIn) {
      stopActivityTracking();
    }
  }, [isLoggedIn, stopActivityTracking]);

  return (
    <AuthContext.Provider value={{ user, isLoggedIn, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};